import { useState, useRef, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { useContent } from '@/stores/RootStore';
import { useHeroParallax } from '@/hooks/useHeroParallax';
import s from './Hero.module.scss';

// ── 히어로: EVS 스마트팩토리 이미지(20초) ↔ mp4(1회 재생) 교대 반복 ──────────
//   [2026-09-05 사용자 요청] 이미지 노출 → mp4 1회 재생 → 다시 이미지 … 무한 반복.
//   ★이미지(blog-hero.png)엔 "EVS / 현장과 데이터를 하나로…" 카피가 중앙에 박혀 있으나,
//    [2026-09-06 사용자 변경] 우리 오버레이 카피(헤드라인·슬로건·자세히 보기)는 이미지·영상 두 구간 모두 '상시 노출'.
//    이유: 이미지 구간에 슬로건·CTA가 사라지는 게 어색. 우리 카피는 하단, 이미지 baked 카피는 중앙이라 위치 안 겹침.
//    (이전엔 텍스트 중복 방지로 이미지 구간에 숨겼었음 → 상시 노출로 전환.)
//   변경 금지 트리거: mp4는 loop 제거(1회 재생 후 ended로 이미지 복귀). autoPlay 대신 코드로 play 제어.
const IMAGE_MS = 20000; // 이미지 노출 시간(사용자 지정 2026-09-09: 20초. 1분→30초→20초로 단축) — 이후 mp4 1회 재생 → 반복

// [2026-09-10 사용자] 히어로 카피를 모드별로: 영상=환경(대기오염) 슬로건 / 정지이미지=안전(체감온도·폭염) 슬로건.
//   환경 카피는 기존 hero.*(콘텐츠). 안전 카피는 아래(체감온도/온열질환 — 우리 작업 근거). 전환은 절제된 fade(copyFade).
const SAFETY_COPY = {
  headline: 'IoT로 폭염·온열질환을 실시간 감시하다',
  slogan: '작업자의 안전을 위한 한 걸음, 실시간 체감온도(WBGT) 관제로 시작하세요.',
  support: '현장 온열질환 예방, 설치부터 관제·A/S까지 함께합니다.',
};

const Hero = observer(() => {
  const { hero } = useContent();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'image' | 'video'>('image');
  const videoRef = useRef<HTMLVideoElement>(null);
  // [2026-09-08 사용자] 메인 히어로 이미지/영상도 스크롤 시 절제된 패럴랙스. img+video를 감싼 .media에 적용.
  const mediaRef = useHeroParallax<HTMLDivElement>({ factor: 0.12, scale: 1.14 });

  useEffect(() => {
    const v = videoRef.current;
    if (mode === 'image') {
      // 이미지 구간: 영상 정지·되감기 후 IMAGE_MS(20초) 뒤 영상 모드로
      if (v) { v.pause(); try { v.currentTime = 0; } catch { /* noop */ } }
      const t = setTimeout(() => setMode('video'), IMAGE_MS);
      return () => clearTimeout(t);
    }
    // 영상 구간: 처음부터 1회 재생, 끝나면(or 오류 시) 이미지로 복귀
    if (!v) return;
    try { v.currentTime = 0; } catch { /* noop */ }
    const back = () => setMode('image');
    v.addEventListener('ended', back);
    v.addEventListener('error', back);
    v.play().catch(() => back()); // 자동재생 차단 등 실패 시 이미지로
    return () => { v.removeEventListener('ended', back); v.removeEventListener('error', back); };
  }, [mode]);

  return (
    <section className={s.hero}>
      {/* 패럴랙스 대상(이미지+영상만) — 오버레이·카피는 밖에 둬서 고정 유지 */}
      <div className={s.media} ref={mediaRef}>
        {/* EVS 스마트팩토리 이미지(자체 카피 포함) */}
        <img
          className={`${s.img} ${mode === 'image' ? s.on : ''}`}
          src="/images/hero/blog-hero.png"
          alt="EVS 스마트팩토리 IoT 솔루션"
          aria-hidden={mode !== 'image'}
        />
        {/* 기존 mp4(1회 재생) */}
        <video
          ref={videoRef}
          className={`${s.video} ${mode === 'video' ? s.on : ''}`}
          muted playsInline preload="auto"
          poster="/video/hero-istock-poster.jpg"
        >
          <source src="/video/hero-istock.mp4" type="video/mp4" />
        </video>
      </div>
      {/* 다크 스크림 — 상시(2026-09-06 사용자: 우리 카피를 이미지 위에도 상시 노출 → 두 구간 모두 가독 필요) */}
      <div className={`${s.overlay} ${s.on}`} />
      {/* 우리 오버레이 텍스트 — 상시 노출(이미지·영상 두 구간 모두). 슬로건·CTA가 사라지지 않게(사용자 지시 2026-09-06).
          ※이미지 자체 baked 카피('현장과 데이터…')는 중앙, 우리 카피는 하단이라 위치는 겹치지 않음. */}
      <div className={`${s.content} ${s.on}`}>
        {/* 모드별 카피(영상=환경 / 정지이미지=안전) — key={mode}로 전환 시 절제된 fade(copyFade)로 리마운트. 레이아웃/구조 동일. */}
        <div key={mode} className={s.copyFade}>
          <h1 className={s.headline}>{mode === 'video' ? hero.headline : SAFETY_COPY.headline}</h1>
          <p className={s.slogan}>{mode === 'video' ? hero.slogan : SAFETY_COPY.slogan}</p>
          <div className={s.support}>
            <p>{mode === 'video' ? hero.support : SAFETY_COPY.support}</p>
            {/* [2026-09-11] 죽은 링크(#) 수정 — 모드별 연결: 영상=환경 카피→/environment, 정지이미지=안전 카피→/safety */}
            <a href={mode === 'video' ? '/environment' : '/safety'} className={s.cta}
              onClick={(e) => { e.preventDefault(); navigate(mode === 'video' ? '/environment' : '/safety'); }}>
              {hero.cta}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M7 17L17 7M17 7H9M17 7v8" />
              </svg>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
});

export default Hero;
